import { morphSectionTargets, sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';
import { buildFacetsPreviewURL, createDrawerPreviewCountController } from './facets-drawer-preview.js';
import { configurePriceFacetComponent } from './facets-price-component.js';
import { FacetStatusComponent } from './facet-status-component.js';

const FACET_PREFETCH_ENABLED = false;

function syncFilterClearButton(clearButton, className, event) {
  if (clearButton instanceof Element) {
    clearButton.classList.toggle(className, event.shouldShowClearAll());
  }
}

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

let drawerPreviewCountController = null;
let facetUpdateSequence = 0;

/**
 * @returns {FacetsFormComponent | null}
 */
function getDrawerFacetsFormComponent() {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return null;

  const facetsForm = drawer.querySelector('facets-form-component');
  return facetsForm instanceof FacetsFormComponent ? facetsForm : null;
}

function getDrawerPreviewCountController() {
  if (!drawerPreviewCountController) {
    drawerPreviewCountController = createDrawerPreviewCountController(getDrawerFacetsFormComponent);
  }
  return drawerPreviewCountController;
}

/**
 * @param {FacetsFormComponent} facetsForm
 */
function scheduleDrawerSeeResultsCountUpdate(facetsForm) {
  getDrawerPreviewCountController().schedule(facetsForm);
}

function cancelDrawerPreviewCountWork() {
  getDrawerPreviewCountController().cancel();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => getDrawerPreviewCountController().bind());
} else {
  getDrawerPreviewCountController().bind();
}

function bindDrawerApplyButtonOnce() {
  if (document.documentElement.dataset.facetsDrawerApplyBound === 'true') return;
  document.documentElement.dataset.facetsDrawerApplyBound = 'true';

  let lastApplyTime = 0;
  const applyFromDrawerButton = (event) => {
    if (!(event.target instanceof Element)) return;

    const applyButton = event.target.closest('#filters-drawer .facets__see-results');
    if (!(applyButton instanceof HTMLElement)) return;

    const now = Date.now();
    if (now - lastApplyTime < 700) {
      if (event.cancelable) event.preventDefault();
      return;
    }
    lastApplyTime = now;

    const drawerFacetsForm = applyButton.closest('#filters-drawer')?.querySelector('facets-form-component');
    if (!(drawerFacetsForm instanceof FacetsFormComponent)) return;

    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    drawerFacetsForm.updateFilters();
  };

  document.addEventListener('pointerup', applyFromDrawerButton, true);
  document.addEventListener('click', applyFromDrawerButton, true);
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('#filters-drawer')) return;

    const drawerFacetsForm = form.closest('facets-form-component');
    if (!(drawerFacetsForm instanceof FacetsFormComponent)) return;

    if (event.cancelable) event.preventDefault();
    drawerFacetsForm.updateFilters();
  }, true);
}

function bindActiveFilterRemoveLinksOnce() {
  if (document.documentElement.dataset.facetsActiveRemoveLinksBound === 'true') return;
  document.documentElement.dataset.facetsActiveRemoveLinksBound = 'true';

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-active-filter-remove]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = link.dataset.url || link.href;
    const facetsForm = link.closest('facets-form-component') || getPrimaryFacetsFormComponent();
    if (!(facetsForm instanceof FacetsFormComponent) || !url) return;

    if (event.cancelable) event.preventDefault();
    facetsForm.updateFiltersByURL(url);
  }, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindDrawerApplyButtonOnce);
  document.addEventListener('DOMContentLoaded', bindActiveFilterRemoveLinksOnce);
} else {
  bindDrawerApplyButtonOnce();
  bindActiveFilterRemoveLinksOnce();
}

document.addEventListener('shopify:section:load', () => {
  const drawer = document.getElementById('filters-drawer');
  if (drawer) {
    drawer.dataset.previewCountBound = 'false';
  }
  getDrawerPreviewCountController().bind();
});

function getResultsListTargets() {
  return Array.from(document.querySelectorAll('results-list'));
}

let resultsListLoadingCount = 0;
let resultsListLoadingSafetyTimer = 0;

function setResultsListLoadingState(isLoading) {
  getResultsListTargets().forEach((target) => {
    target.classList.toggle('is-content-loading', Boolean(isLoading));
    target.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  });
}

function beginResultsListLoading() {
  resultsListLoadingCount += 1;
  const token = resultsListLoadingCount;
  window.clearTimeout(resultsListLoadingSafetyTimer);
  resultsListLoadingSafetyTimer = window.setTimeout(() => {
    resultsListLoadingCount = 0;
    setResultsListLoadingState(false);
  }, 10000);
  setResultsListLoadingState(true);
  return token;
}

function finishResultsListLoading() {
  resultsListLoadingCount = Math.max(0, resultsListLoadingCount - 1);
  if (resultsListLoadingCount > 0) return;
  window.clearTimeout(resultsListLoadingSafetyTimer);
  setResultsListLoadingState(false);
}

function resetResultsListLoading() {
  resultsListLoadingCount = 0;
  window.clearTimeout(resultsListLoadingSafetyTimer);
  setResultsListLoadingState(false);
}

function scheduleResultsListLoadingReset() {
  resetResultsListLoading();
  window.requestAnimationFrame(() => {
    resetResultsListLoading();
    window.requestAnimationFrame(resetResultsListLoading);
  });
}

if (!document.documentElement.dataset.resultsListLoadingBound) {
  document.documentElement.dataset.resultsListLoadingBound = 'true';
  document.addEventListener('shopify:section:load', scheduleResultsListLoadingReset);
  document.addEventListener('filter:update', scheduleResultsListLoadingReset);
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    this.#setDefaultSorting();
    bindFacetsPopstateOnce();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // No cleanup needed - updateFilters is not debounced anymore
  }

  /**
   * Sets default sorting to manual if no sorting is selected
   */
  #setDefaultSorting() {
    const url = new URL(window.location.href);
    const currentSortBy = url.searchParams.get('sort_by');
    
    // If no sorting is selected, set to manual
    if (!currentSortBy) {
      // Find the manual option in the form
      const sortInputs = this.querySelectorAll('input[name="sort_by"]');
      
      for (const input of sortInputs) {
        if (input instanceof HTMLInputElement) {
          // Look for manual sorting
          if (input.value === 'manual') {
            // Select this option
            input.checked = true;
            
            // Update select element if it exists
            const sortSelect = this.querySelector('select[name="sort_by"]');
            if (sortSelect instanceof HTMLSelectElement) {
              sortSelect.value = input.value;
            }
            
            // НЕ вызываем updateFilters при инициализации - это блокирует загрузку
            break;
          }
        }
      }
      
      // If no manual option found, just set the first available option
      if (!url.searchParams.get('sort_by')) {
        const firstInput = this.querySelector('input[name="sort_by"]');
        if (firstInput instanceof HTMLInputElement) {
          firstInput.checked = true;
        
          const sortSelect = this.querySelector('select[name="sort_by"]');
          if (sortSelect instanceof HTMLSelectElement) {
            sortSelect.value = firstInput.value;
          }
          
          // НЕ вызываем updateFilters - только устанавливаем значение
        }
      }
    } else {
      // If sort_by is already set, just update the form elements to match
      const sortInputs = this.querySelectorAll('input[name="sort_by"]');
      const sortSelect = this.querySelector('select[name="sort_by"]');
      
      for (const input of sortInputs) {
        if (input instanceof HTMLInputElement) {
          input.checked = (input.value === currentSortBy);
        }
      }
      
      if (sortSelect instanceof HTMLSelectElement) {
        sortSelect.value = currentSortBy;
      }
    }
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));
    const currentParameters = new URL(window.location.href).searchParams;

    if (!newParameters.has('sort_by') && currentParameters.has('sort_by')) {
      newParameters.set('sort_by', currentParameters.get('sort_by'));
    }

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');
    if (newParameters.get('sort_by') === 'manual') newParameters.delete('sort_by');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Creates URL parameters from an existing URL or URL string.
   * Keeps browser history state and facet events in sync when filters are changed
   * outside of the main form, such as related collection cards or back/forward navigation.
   *
   * @param {URL | string} urlSource
   * @returns {URLSearchParams}
   */
  createURLParametersFromURL(urlSource) {
    const url =
      urlSource instanceof URL
        ? new URL(urlSource.toString())
        : new URL(String(urlSource), window.location.origin);

    return new URLSearchParams(url.search);
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  #getResultsURL() {
    const action = this.refs.facetsForm?.getAttribute('action');
    const url = new URL(action || window.location.href, window.location.origin);
    const currentURL = new URL(window.location.href);

    url.search = currentURL.search;
    url.hash = '';

    return url;
  }

  #buildResultsURL(parameters = this.createURLParameters()) {
    const action = this.refs.facetsForm?.getAttribute('action');
    const url = new URL(action || window.location.href, window.location.origin);
    const paramGroups = new Map();

    url.search = '';

    for (const [param, value] of parameters.entries()) {
      if (!paramGroups.has(param)) {
        paramGroups.set(param, []);
      }
      paramGroups.get(param).push(value);
    }

    for (const [param, values] of paramGroups.entries()) {
      url.searchParams.delete(param);
      for (const value of values) {
        url.searchParams.append(param, value);
      }
    }

    url.hash = '';
    return url;
  }

  #collectFilterParameters(baseParameters = this.createURLParameters()) {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return baseParameters;

    const formId = form.id;
    const parameters = new URLSearchParams();
    const currentParameters = new URL(window.location.href).searchParams;

    for (const [key, value] of baseParameters.entries()) {
      if (!key.startsWith('filter.')) {
        parameters.append(key, value);
      }
    }

    const controls = document.querySelectorAll(
      `#${CSS.escape(formId)} [name], [form="${formId}"][name]`
    );

    for (const control of controls) {
      if (
        !(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (control.disabled || !control.name || !control.name.startsWith('filter.')) continue;

      if (control instanceof HTMLInputElement) {
        const type = control.type;
        if (type === 'range' || type === 'button' || type === 'submit' || type === 'reset') continue;
        if ((type === 'checkbox' || type === 'radio') && !control.checked) continue;
      }

      if (control instanceof HTMLSelectElement && control.multiple) {
        Array.from(control.selectedOptions).forEach((option) => {
          if (option.value !== '') parameters.append(control.name, option.value);
        });
        continue;
      }

      if (control.value !== '') {
        parameters.append(control.name, control.value);
      }
    }

    if (!parameters.has('sort_by') && currentParameters.has('sort_by')) {
      parameters.set('sort_by', currentParameters.get('sort_by'));
    }

    if (parameters.get('filter.v.price.gte') === '') parameters.delete('filter.v.price.gte');
    if (parameters.get('filter.v.price.lte') === '') parameters.delete('filter.v.price.lte');

    parameters.delete('page');
    if (parameters.get('sort_by') === 'manual') parameters.delete('sort_by');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) parameters.set(SEARCH_QUERY, searchQuery);

    return parameters;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash(resultsURL = this.#buildResultsURL(this.#collectFilterParameters())) {
    history.pushState({ urlParameters: resultsURL.searchParams.toString() }, '', resultsURL.toString());
  }

  #navigateToFilterURL(url) {
    const nextURL = url instanceof URL ? new URL(url.toString()) : new URL(String(url), window.location.origin);
    nextURL.searchParams.delete('section_id');
    nextURL.hash = '';

    const href = nextURL.toString();
    if (href === window.location.href) {
      window.location.reload();
      return;
    }

    window.location.assign(href);
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    cancelDrawerPreviewCountWork();
    if (this.closest('#filters-drawer')) {
      return this.updateFiltersByURL(buildFacetsPreviewURL(this).toString());
    }

    const resultsURL = this.#buildResultsURL(this.#collectFilterParameters());
    this.dispatchEvent(new FilterUpdateEvent(resultsURL.searchParams));
    this.#navigateToFilterURL(resultsURL);
  };

  /**
   * Updates the section
   */
  async #updateSection({ renderTargets, url } = {}) {
    const updateSequence = ++facetUpdateSequence;
    const loadingToken = beginResultsListLoading();
    const sectionId = this.sectionId;
    const resultsURL = url instanceof URL ? url : this.#getResultsURL();
    try {
      const drawer = /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));
      if (drawer && drawer.open && drawer.classList.contains('facets--drawer')) {
        const dialogComponent = /** @type {import('./dialog.js').DialogComponent | null} */ (drawer.closest('dialog-component'));
        if (dialogComponent) {
          try {
            await dialogComponent.closeDialog();
          } catch (error) {
            console.warn('Failed to close facets drawer dialog', error);
          }
        }
      }

      if (renderTargets?.length) {
        const html = await sectionRenderer.getSectionHTML(sectionId, false, resultsURL);
        morphSectionTargets(sectionId, html, renderTargets);
      } else {
        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          replace: true,
          url: resultsURL,
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Failed to update filtered collection section', error);

      if (updateSequence === facetUpdateSequence && this.isConnected) {
        window.location.assign(window.location.href);
      }
    } finally {
      finishResultsListLoading(loadingToken);
      scheduleResultsListLoadingReset();
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url, { updateHistory = true, preserveSort = updateHistory, renderTargets } = {}) {
    const nextURL = url instanceof URL ? new URL(url.toString()) : new URL(String(url), window.location.origin);
    const currentURL = new URL(window.location.href);

    if (preserveSort && !nextURL.searchParams.has('sort_by') && currentURL.searchParams.has('sort_by')) {
      nextURL.searchParams.set('sort_by', currentURL.searchParams.get('sort_by'));
    }

    if (updateHistory) nextURL.searchParams.delete('page');

    this.dispatchEvent(new FilterUpdateEvent(this.createURLParametersFromURL(nextURL)));
    this.#navigateToFilterURL(nextURL);
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

function getPrimaryFacetsFormComponent() {
  const forms = Array.from(document.querySelectorAll('facets-form-component'));
  return forms.find((form) => !form.closest('dialog')) || forms[0] || null;
}

function bindFacetsPopstateOnce() {
  if (document.documentElement.dataset.facetsPopstateBound === 'true') return;
  document.documentElement.dataset.facetsPopstateBound = 'true';

  window.addEventListener('popstate', () => {
    const facetsForm = getPrimaryFacetsFormComponent();
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(window.location.href, { updateHistory: false });
  });
}

function getClosestFacetsFormComponent(element) {
  const facetsForm = element.closest('facets-form-component');
  return facetsForm instanceof FacetsFormComponent ? facetsForm : null;
}

configurePriceFacetComponent({
  getFacetsForm: getClosestFacetsFormComponent,
  scheduleDrawerSeeResultsCountUpdate
});

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  // Cache for prefetched pages and abort controller
  static prefetchCache = new Map();
  static prefetchAbortController = null;

  connectedCallback() {
    super.connectedCallback();
  }

  #performUpdate() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    if (this.dataset.deferApply === 'true') {
      this.#updateSelectedFacetSummary();
      scheduleDrawerSeeResultsCountUpdate(facetsForm);
      return;
    }

    this.#performUpdate();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels - DISABLED by default for performance
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!FACET_PREFETCH_ENABLED) return;
    
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    const urlString = url.toString();

    // Cancel previous prefetch request
    if (FacetInputsComponent.prefetchAbortController) {
      FacetInputsComponent.prefetchAbortController.abort();
    }

    if (FacetInputsComponent.prefetchCache.has(urlString)) {
      return;
    }

    FacetInputsComponent.prefetchAbortController = new AbortController();

    sectionRenderer.getSectionHTML(this.sectionId, true, url, {
      signal: FacetInputsComponent.prefetchAbortController.signal
    }).then(() => {
      FacetInputsComponent.prefetchCache.set(urlString, true);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        console.warn('Prefetch failed:', err);
      }
    });
  }, 800); // Debounce for prefetch to avoid too many requests

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    syncFilterClearButton(this.refs.clearButton, 'facets__clear--active', event);
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component') || getPrimaryFacetsFormComponent();

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  #handleFilterUpdate = (event) => {
    syncFilterClearButton(this.refs.clearButton, 'active', event);
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup if needed
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}
